import { NgModule, inject } from "@angular/core";
import { IntegrationTestRuntimeService } from "./testing/integration-test-runtime";
import { RouterModule, Routes } from "@angular/router";

import { AuthLocalGuard } from "./guards/auth-local.guard";
import { DeleteAccountGuard } from "./guards/delete-account.guard";
import { LandingGuard } from "./guards/landing.guard";
import { MainLocalGuard } from "./guards/main-local.guard";
import { StateGuard } from "./guards/state.guard";
import { OpenActiveGuard } from "./guards/open-active.guard";
import { AssistantLaunchAuthGuard } from "./guards/assistant-launch-auth.guard";
import { optimizedIdCardCanMatchGuard } from "./guards/optimized-id-card-can-match.guard";
import {
  AppPreloadingStrategy,
  SKIP_ROUTE_PRELOAD,
} from "./shared/services/app-preloading-strategy.service";

export const APP_ROUTES: Routes = [
  {
    path: "sticky-footer-proof",
    canMatch: [() => inject(IntegrationTestRuntimeService).isEnabled()],
    loadComponent: () =>
      import("./testing/sticky-footer-proof.component").then(
        (module) => module.StickyFooterProofComponent
      ),
  },
  {
    path: "identity-verification",
    loadChildren: () =>
      import("./onboarding/identity-verification/identity-verification.routes").then(
        (module) => module.IDENTITY_VERIFICATION_ROUTES
      ),
    canActivate: [AuthLocalGuard],
    data: { [SKIP_ROUTE_PRELOAD]: true },
  },
  {
    path: "",
    redirectTo: "onboarding-intro",
    pathMatch: "full",
    data: { queryParamsHandling: "merge" },
  },
  {
    path: "onboarding-intro",
    loadChildren: () =>
      import("./pages/onboarding-intro/onboarding-intro.routes").then(
        (m) => m.onboardingIntroRoutes
      ),
    canActivate: [AuthLocalGuard],
    data: { queryParamsHandling: "merge" },
  },
  {
    path: "onboarding-intro/:promo-code",
    loadChildren: () =>
      import("./pages/onboarding-intro/onboarding-intro.routes").then(
        (m) => m.onboardingIntroRoutes
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "questionnaire",
    loadChildren: () =>
      import("./onboarding/questionnaire/questionnaire.module").then(
        (m) => m.QuestionnairePageModule
      ),
    canActivate: [StateGuard],
  },
  {
    path: "terms-of-service",
    loadChildren: () =>
      import("./onboarding/terms-of-service/terms-of-service.module").then(
        (m) => m.TermsOfServicePageModule
      ),
    canActivate: [StateGuard],
  },
  {
    path: "credentials",
    loadChildren: () =>
      import("./onboarding/credentials/credentials.module").then(
        (m) => m.CredentialsPageModule
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "id-card-verification",
    loadChildren: () =>
      import("./onboarding/id-card-verification-optimized/id-card-verification-optimized.module").then(
        (m) => m.IdCardVerificationOptimizedModule
      ),
    canMatch: [optimizedIdCardCanMatchGuard],
    canActivate: [AuthLocalGuard],
    data: { [SKIP_ROUTE_PRELOAD]: true },
  },
  {
    path: "id-card-verification",
    loadChildren: () =>
      import("./onboarding/id-card-verification/id-card-verification.module").then(
        (m) => m.IdCardVerificationPageModule
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "info-cross-check",
    loadChildren: () =>
      import("./onboarding/info-cross-check/info-cross-check.module").then(
        (m) => m.InfoCrossCheckPageModule
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "ktp-scan",
    loadChildren: () =>
      import("./onboarding/ktp-scan/ktp-scan.module").then(
        (m) => m.KtpScanPageModule
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "additional-info",
    loadChildren: () =>
      import("./pages/additional-info/additional-info.routes").then(
        (m) => m.additionalInfoRoutes
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "additional-profile",
    loadChildren: () =>
      import("./onboarding/additional-profile/additional-profile.module").then(
        (m) => m.AdditionalProfilePageModule
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "additional-payday-info",
    loadChildren: () =>
      import("./onboarding/additional-payday-info/additional-payday-info.module").then(
        (m) => m.AdditionalPaydayInfoPageModule
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "employment",
    loadChildren: () =>
      import("./onboarding/employment/employment.module").then(
        (m) => m.EmploymentPageModule
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "employment-verification",
    loadChildren: () =>
      import("./onboarding/employment-verification/employment-verification.module").then(
        (m) => m.EmploymentVerificationPageModule
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "smile",
    loadChildren: () =>
      import("./pages/smile/smile.routes").then((m) => m.smileRoutes),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "bank-statements",
    loadChildren: () =>
      import("./pages/bank-statements/bank-statements.routes").then(
        (m) => m.bankStatementsRoutes
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "finverse",
    loadChildren: () =>
      import("./pages/finverse/finverse.routes").then((m) => m.finverseRoutes),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "truid",
    loadChildren: () =>
      import("./pages/truid/truid.routes").then((m) => m.truidRoutes),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "link-bank-account",
    loadChildren: () =>
      import("./pages/link-bank/link-bank.routes").then(
        (m) => m.linkBankRoutes
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "onboarding-final-screen",
    loadChildren: () =>
      import("./pages/onboarding-final-screen/onboarding-final-screen.routes").then(
        (m) => m.onboardingFinalScreenRoutes
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "main",
    loadChildren: () =>
      import("./main/main.module").then((m) => m.MainPageModule),
    canActivate: [AuthLocalGuard, MainLocalGuard],
  },
  {
    path: "login",
    loadChildren: () =>
      import("./login/login.routes").then((m) => m.loginRoutes),
  },
  {
    path: "application-rejected",
    loadChildren: () =>
      import("./pages/user-rejected/user-rejected.routes").then(
        (m) => m.userRejectedRoutes
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "account-recovery",
    loadChildren: () =>
      import("./pages/account-recovery/account-recovery.routes").then(
        (m) => m.accountRecoveryRoutes
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "permanently-deactivated",
    loadChildren: () =>
      import("./pages/permanently-deactivated/permanently-deactivated.routes").then(
        (m) => m.permanentlyDeactivatedRoutes
      ),
    canActivate: [AuthLocalGuard],
  },
  {
    path: "delete-account",
    loadChildren: () =>
      import("./pages/delete-account/delete-account.routes").then(
        (m) => m.deleteAccountRoutes
      ),
    canActivate: [DeleteAccountGuard],
  },
  {
    path: "assistant-launch",
    loadChildren: () =>
      import("./pages/assistant-launch/assistant-launch.routes").then(
        (m) => m.assistantLaunchRoutes
      ),
    canActivate: [AssistantLaunchAuthGuard],
  },
  {
    path: "redirect-to-app",
    loadChildren: () =>
      import("./main/pages/AppRedirect/app-redirect.module").then(
        (m) => m.AppRedirectModule
      ),
  },
  {
    path: "line-success",
    loadChildren: () =>
      import("./main/pages/line-login-success/line-login-success.module").then(
        (m) => m.LineLoginSuccessModule
      ),
  },
  {
    path: "landing",
    loadChildren: () =>
      import("./landing/landing.module").then((m) => m.LandingPageModule),
    canActivate: [LandingGuard],
  },
  {
    path: "open-onboarding",
    loadChildren: () =>
      import("./pages/open-onboarding/open-onboarding.routes").then(
        (m) => m.openOnboardingRoutes
      ),
    canActivate: [OpenActiveGuard],
  },
  {
    path: "**",
    pathMatch: "full",
    redirectTo: "onboarding-intro",
    data: { queryParamsHandling: "merge" },
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(APP_ROUTES, {
      preloadingStrategy: AppPreloadingStrategy,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
